'use client'
import React, { useLayoutEffect, useRef } from 'react'
import { cn } from '../utils'

interface TruncateTextProps {
  text: string
  className?: string
  maxLine?: 1 | 2 | 3 | 4
}

function getLineHeightInPixels(element: HTMLDivElement) {
  const computedStyle = window.getComputedStyle(element)
  const lineHeight = computedStyle.lineHeight

  if (lineHeight.endsWith('px')) {
    return parseFloat(lineHeight)
  }

  const fontSize = parseFloat(computedStyle.fontSize)

  if (lineHeight === 'normal') {
    // 浏览器默认行高（通常为 1.2，但不同字体/浏览器可能不同）
    return fontSize * 1.5
  }

  // 处理无单位数值（如 1.5）
  return fontSize * parseFloat(lineHeight)
}

export function debounce<T extends (...args: never[]) => void>(func: T, wait = 200): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return async function (...args: Parameters<T>) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this

    if (timeout !== null) {
      clearTimeout(timeout)
    }

    timeout = setTimeout(() => {
      func.apply(context, args) // 延迟执行
    }, wait)
  }
}

export const TruncateText: React.FC<TruncateTextProps> = ({ text, className, maxLine }) => {
  const textRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const truncate = debounce(() => {
      const element = textRef.current

      if (!element) return

      let newText = text
      element.textContent = text
      const words = text.split(' ')

      const handler = () => {
        words.pop()
        newText = words.join(' ')
        element.textContent = newText.trim() + '…'
      }
      if (!maxLine) {
        while (element.scrollHeight > element.clientHeight && words.length > 0) {
          handler()
        }
      } else {
        const lineHeight = getLineHeightInPixels(element)
        const maxHeight = Math.floor(lineHeight * (maxLine + 0.2))
        while (element.scrollHeight > maxHeight && words.length > 0) {
          handler()
        }
      }
    })

    truncate()

    window.addEventListener('resize', truncate)
    return () => window.removeEventListener('resize', truncate)
  }, [text])

  return (
    <div
      ref={textRef}
      className={cn(
        'overflow-hidden',
        {
          'line-clamp-1': maxLine === 1,
          'line-clamp-2': maxLine === 2,
          'line-clamp-3': maxLine === 3,
          'line-clamp-4': maxLine === 4,
        },
        className,
      )}
      title={text}
    >
      {text}
    </div>
  )
}
